import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, Link } from "@tanstack/react-router";
import { CheckCircle2, Loader2, Mail } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { useForgotPassword } from "@/lib/hooks/auth";
import { PasswordForgotRequestSchema, type PasswordForgotRequest } from "@/lib/schemas/auth";

/**
 * /forgot-password — richiesta reset password (public, no auth guard).
 *
 * Anti-enumeration: il BE risponde SEMPRE 202; mostriamo un messaggio generico
 * ("se l'account esiste, ti abbiamo inviato un link") a prescindere. Il link
 * arriva via email (outbox `auth.password_reset_email`) e punta a
 * /password/reset/$token. Per gli account con MFA il reset chiede anche l'OTP.
 */
export const Route = createFileRoute("/forgot-password")({
  component: ForgotPasswordPage,
});

function ForgotPasswordPage(): JSX.Element {
  const { t } = useTranslation();
  const forgot = useForgotPassword();
  const [sent, setSent] = useState(false);

  const form = useForm<PasswordForgotRequest>({
    resolver: zodResolver(PasswordForgotRequestSchema),
    defaultValues: { email: "" },
  });

  async function onSubmit(values: PasswordForgotRequest): Promise<void> {
    try {
      await forgot.mutateAsync(values);
      setSent(true);
    } catch {
      // surfaced via forgot.error block
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-muted/30 p-6">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-2 pb-2">
          <CardTitle className="flex items-center gap-2">
            <Mail className="size-5 text-muted-foreground" aria-hidden />
            {t("forgot.title")}
          </CardTitle>
          <CardDescription>{t("forgot.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {sent ? (
            <div className="rounded-md border border-success/40 bg-success/5 p-3 text-sm text-success">
              <div className="flex items-center gap-2 font-medium">
                <CheckCircle2 className="size-4" aria-hidden />
                {t("forgot.sent_title")}
              </div>
              <p className="mt-1 text-xs">{t("forgot.sent_hint")}</p>
              <Link to="/system" className="mt-2 inline-block text-xs text-primary underline">
                {t("forgot.back_to_login")}
              </Link>
            </div>
          ) : (
            <form
              onSubmit={(e) => {
                void form.handleSubmit(onSubmit)(e);
              }}
              className="space-y-3"
              noValidate
            >
              <div className="space-y-1">
                <Label htmlFor="email" className="text-xs font-medium">
                  {t("login.local.email_label")}
                </Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="username"
                  autoFocus
                  disabled={forgot.isPending}
                  {...form.register("email")}
                />
                {form.formState.errors.email?.message && (
                  <p className="text-xs text-destructive" role="alert">
                    {t(String(form.formState.errors.email.message))}
                  </p>
                )}
              </div>

              {forgot.isError && (
                <div className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
                  <ul className="ml-4 list-disc space-y-0.5">
                    {formatApiError(forgot.error).map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                </div>
              )}

              <Button type="submit" className="w-full" disabled={forgot.isPending}>
                {forgot.isPending && <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />}
                {t("forgot.submit")}
              </Button>
              <p className="pt-1 text-center text-xs text-muted-foreground">
                <Link to="/system" className="underline underline-offset-2 hover:text-foreground">
                  {t("forgot.back_to_login")}
                </Link>
              </p>
            </form>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
